import { useMemo } from "react"
import {
  Box,
  Typography,
  Skeleton,
  useTheme,
  CardMedia,
  Button,
  Avatar,
} from "@mui/material"
import { deepmerge } from "@mui/utils"

import { createOpeningHours } from "utils/date"
import { useTranslation } from "providers/i18n"

import { ClockIcon, LayerIcon } from "extensions/one-siam/components/icons"

const LoadingFeature = () => {
  const theme = useTheme()
  const {
    root,
    titleContainer,
    actionContainer,
    headerContainer,
    infoContainer,
    infoList,
    infoListItem,
  } = theme.components[`NextopiaSummary`]?.styleOverrides || {}
  return (
    <Box sx={root}>
      <Box sx={headerContainer}>
        <Box sx={titleContainer}>
          <Skeleton variant="rectangular" width={32} height={32} />
          <Skeleton variant="text" width={120} />
        </Box>
        <Box sx={actionContainer}>
          <Skeleton variant="rounded" width={74} height={32} />
        </Box>
      </Box>

      <Box sx={infoContainer}>
        <Box sx={infoList}>
          <Box sx={infoListItem}>
            <Skeleton variant="text" width={70} />
          </Box>
          <Box sx={infoListItem}>
            <Skeleton variant="text" width={115} />
          </Box>
          <Box sx={infoListItem}>
            <Skeleton variant="text" width={115} />
          </Box>
        </Box>
        <Skeleton variant="rounded" width={60} height={40} />
      </Box>
    </Box>
  )
}

export const NextopiaSummary = ({
  zone,
  logo,
  venueLogo,
  coverImage,
  category,
  phone,
  level,
  name,
  venueName,
  hours,
  id,
  loading,
  group,
  onNavigate,
  onViewAllShops,
  categoryIconProp = {},
  sx = {},
  ...props
}) => {
  const { t } = useTranslation({ defaultValue: null })
  const {
    close = "",
    open = "",
    isValid,
    openingStatus,
    nextOpeningStatus,
    dayToOpen,
    isOpening,
  } = useMemo(() => createOpeningHours(hours), [hours])

  const theme = useTheme()
  const {
    root,
    innerContent,
    titleContainer,
    venueLogoContainer,
    venueLogoImage,
    logoContainer,
    logoImage,
    actionContainer,
    actionButton,
    headerContainer,
    infoContainer,
    infoList,
    infoListItem,
    infoText,
    iconWrapper,
  } = theme.components[`NextopiaSummary`]?.styleOverrides || {}

  const venueNameText = t(venueName)
  const categoryText = t(category)
  const levelText = level ? t("Level Text", { level: t(level) }) : "-"
  const openingHoursText = isValid
    ? `${t(openingStatus)} · ${t(nextOpeningStatus)} ${dayToOpen} ${
        isOpening ? close : open
      }`
    : "-"

  if (loading) return <LoadingFeature />

  return (
    <Box sx={deepmerge(root, sx)} {...props}>
      <Box
        sx={deepmerge(innerContent, {
          backgroundImage: `linear-gradient(0deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.90) 65%), url(${coverImage})`,
        })}
      >
        <Box sx={headerContainer}>
          <Box sx={titleContainer}>
            <Box sx={logoContainer}>
              <Avatar sx={logoImage} src={logo} />
            </Box>
          </Box>
        </Box>
        <Box sx={infoContainer}>
          <Box sx={infoList}>
            <Box
              sx={infoListItem}
              className="FeatureSummary-infoListItem--category"
            >
              <Typography
                variant="subtitle2"
                className="FeatureSummary-infoText"
                sx={infoText}
              >
                {categoryText}
              </Typography>
            </Box>
            <Box
              sx={infoListItem}
              className="FeatureSummary-infoListItem--venue"
            >
              <Typography
                variant="subtitle2"
                className="FeatureSummary-infoText"
                sx={infoText}
              >
                {venueNameText}
              </Typography>
            </Box>
            <Box
              sx={infoListItem}
              className="FeatureSummary-infoListItem--level"
            >
              <Box sx={iconWrapper}>
                <LayerIcon />
              </Box>
              <Typography
                variant="subtitle2"
                className="FeatureSummary-"
                sx={infoText}
              >
                {levelText}
              </Typography>
            </Box>
            <Box
              sx={infoListItem}
              className="FeatureSummary-infoListItem--opening-hours"
            >
              <Box sx={iconWrapper}>
                <ClockIcon />
              </Box>
              <Typography
                variant="subtitle2"
                className="FeatureSummary-infoText"
                sx={infoText}
              >
                {openingHoursText}
              </Typography>
            </Box>
          </Box>
          {venueLogo && (
            <Box sx={venueLogoContainer}>
              <CardMedia
                component="img"
                image={venueLogo}
                sx={venueLogoImage}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={actionContainer}>
        <Button
          variant="contained"
          color="secondary"
          onClick={onViewAllShops}
          sx={actionButton}
        >
          {t("View all shops")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onNavigate}
          sx={actionButton}
        >
          {t("Get direction")}
        </Button>
      </Box>
    </Box>
  )
}
